// App root
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";

import { useShopifyCartSync } from "@/hooks/useShopifyCartSync";
import { CookieConsent } from "@/components/CookieConsent";
import { TrackingScripts } from "@/components/TrackingScripts";
import Index from "./pages/Index";

import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import NotFound from "./pages/NotFound";
import { lazy, Suspense } from "react";
import { AdminRoute } from "@/components/auth/AdminRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Lazy load pages
const FeedManager = lazy(() => import("./pages/admin/FeedManager"));
const SEODashboard = lazy(() => import("./pages/admin/SEODashboard"));
const LinkSuggestions = lazy(() => import("./pages/admin/LinkSuggestions"));
const ErrorDashboard = lazy(() => import("./pages/admin/ErrorDashboard"));
const ShopifyProductDetail = lazy(() => import("./pages/ShopifyProductDetail"));
const ShopifyCatalog = lazy(() => import("./pages/ShopifyCatalog"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const Auth = lazy(() => import("./pages/Auth"));
const Account = lazy(() => import("./pages/Account"));
const Tracking = lazy(() => import("./pages/Tracking"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ShippingPage = lazy(() => import("./pages/ShippingPage"));
const ReturnsPage = lazy(() => import("./pages/ReturnsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const TrustPage = lazy(() => import("./pages/TrustPage"));
const BusinessInfoPage = lazy(() => import("./pages/BusinessInfoPage"));
const DealsPage = lazy(() => import("./pages/DealsPage"));
const BestGuidePage = lazy(() => import("./pages/BestGuidePage"));


const ProductRedirect = () => {
  const { handle } = useParams<{ handle: string }>();
  return <Navigate to={`/product/${handle}`} replace />;
};

const queryClient = new QueryClient();

const ShopifyCartSyncWrapper = () => { useShopifyCartSync(); return null; };

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ShopifyCartSyncWrapper />
          <TrackingScripts />
          <CookieConsent />
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/catalog" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><ShopifyCatalog /></Suspense>} />
            <Route path="/catalog/:category" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><ShopifyCatalog /></Suspense>} />
            <Route path="/catalog/:category/:subcategory" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><ShopifyCatalog /></Suspense>} />
            <Route path="/product/:slug" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><ShopifyProductDetail /></Suspense>} />
            <Route path="/cart" element={<Navigate to="/" replace />} />
            <Route path="/checkout" element={<Navigate to="/" replace />} />
            <Route path="/confirmation" element={<OrderConfirmation />} />
            <Route path="/order-confirmation" element={<OrderConfirmation />} />
            <Route path="/deals" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><DealsPage /></Suspense>} />
            <Route path="/best/:slug" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><BestGuidePage /></Suspense>} />
            <Route path="/shopify" element={<Navigate to="/catalog" replace />} />
            <Route path="/shopify-product/:handle" element={<ProductRedirect />} />
            {/* Blog routes */}
            <Route path="/blog" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><Blog /></Suspense>} />
            <Route path="/blog/:slug" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><BlogArticle /></Suspense>} />
            {/* Auth & user routes */}
            <Route path="/auth" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><Auth /></Suspense>} />
            <Route path="/account" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><Account /></Suspense>} />
            <Route path="/tracking" element={<ErrorBoundary context="tracking"><Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><Tracking /></Suspense></ErrorBoundary>} />
            <Route path="/wishlist" element={<ErrorBoundary context="wishlist"><Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><Wishlist /></Suspense></ErrorBoundary>} />
            <Route path="/about" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><AboutPage /></Suspense>} />
            <Route path="/shipping" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><ShippingPage /></Suspense>} />
            <Route path="/returns" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><ReturnsPage /></Suspense>} />
            <Route path="/privacy" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><PrivacyPage /></Suspense>} />
            <Route path="/terms" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><TermsPage /></Suspense>} />
            <Route path="/contact" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><ContactPage /></Suspense>} />
            <Route path="/trust" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><TrustPage /></Suspense>} />
            <Route path="/business-information" element={<Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}><BusinessInfoPage /></Suspense>} />
            {/* Admin routes */}
            <Route 
              path="/admin/feeds" 
              element={
                <AdminRoute>
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}>
                    <FeedManager />
                  </Suspense>
                </AdminRoute>
              } 
            />
            <Route
              path="/admin/seo"
              element={
                <AdminRoute>
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}>
                    <SEODashboard />
                  </Suspense>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/link-suggestions"
              element={
                <AdminRoute>
                  <Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}>
                    <LinkSuggestions />
                  </Suspense>
                </AdminRoute>
              }
            />
            <Route
              path="/admin/errors"
              element={
                <AdminRoute>
                  <ErrorBoundary context="admin">
                    <Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}>
                      <ErrorDashboard />
                    </Suspense>
                  </ErrorBoundary>
                </AdminRoute>
              }
            />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
